import { PlaycastGamepadDevice, PlaycastKeyDetail, PlaycastMouseButton } from '@playcastdotio/CoreTypes';
import { generators, PlaycastMessage, PlaycastMessageTarget } from '@playcastdotio/Messaging';
import { convertGamepads } from '../convertGamepads';
import { isEqual } from 'lodash';
import { getKeyboardKeyLocation, getMouseButton } from '../utility';
import { hostConnectionState, gamepadState } from '../stores';
import { getPeer } from '..';
import { MobileGamepadState, mobileGamepadToGamepadStateMessage } from '@playcastdotio/MobileGamepad';

// Track previous mouse position for delta calculation
let previousMousePosition = { x: 0, y: 0 };
let lastEventTime = 0;

const send = (message: PlaycastMessage<PlaycastMessageTarget>) => {
  const sendMessage = getPeer()?.sendMessage;
  if (sendMessage) {
    sendMessage(message);
  } else {
    console.error('Messaging not set up yet.');
  }
};

let latestGamepadState: PlaycastGamepadDevice[] = [];

export const handleEvent = (event: Event) => {
  if (hostConnectionState.currentState.status === 'connected') {
    const peerId = getPeer()?.peerId;
    if (!peerId) {
      console.error('Peer ID is not defined');
      return;
    }
    switch (event.type) {
      case 'gamepad': {
        const e = event as CustomEvent;
        const capture = e.detail as (Gamepad | null)[];
        if (!isEqual(latestGamepadState, convertGamepads(capture.filter((x) => x) as Gamepad[]))) {
          const filteredGamepadEvents = convertGamepads(capture.filter((x) => x) as Gamepad[]);
          latestGamepadState = filteredGamepadEvents;
          send(
            generators.gamepad.setState(peerId, {
              gamepads: filteredGamepadEvents,
              playerCoordinates: gamepadState.currentState.playerCoordinates,
            }),
          );
        }
        break;
      }

      case 'keydown': {
        const e = event as KeyboardEvent;
        const detail: PlaycastKeyDetail = {
          keyCode: e.which,
          location: getKeyboardKeyLocation(e.location),
          source: {
            type: 'browser',
            code: e.code,
          },
          modifiers: {
            alt: e.altKey,
            ctrl: e.ctrlKey,
            meta: e.metaKey,
            shift: e.shiftKey,
          },
          displayName: e.key,
        };
        send(generators.keyboard.down(peerId, detail));
        break;
      }

      case 'keyup': {
        const e = event as KeyboardEvent;
        const detail: PlaycastKeyDetail = {
          keyCode: e.which,
          location: getKeyboardKeyLocation(e.location),
          source: {
            type: 'browser',
            code: e.code,
          },
          modifiers: {
            alt: e.altKey,
            ctrl: e.ctrlKey,
            meta: e.metaKey,
            shift: e.shiftKey,
          },
          displayName: e.key,
        };
        send(generators.keyboard.up(peerId, detail));
        break;
      }

      case 'mouseup': {
        const e = event as MouseEvent;
        const detail: PlaycastMouseButton = {
          button: getMouseButton(e.button),
          clickCount: 1,
          delta: {
            x: e.movementX,
            y: e.movementY,
          },
          playerCoordinates: {
            origin: 'topLeft',
            dimensions: {
              x: e.clientX,
              y: e.clientY,
            },
            display: {
              x: e.screenX,
              y: e.screenY,
            },
          },
          position: {
            x: 0,
            y: 0,
          },
        };
        send(generators.mouse.up(peerId, detail));
        break;
      }

      case 'mousedown': {
        const e = event as MouseEvent;
        const detail: PlaycastMouseButton = {
          button: getMouseButton(e.button),
          clickCount: 1,
          delta: {
            x: e.movementX,
            y: e.movementY,
          },
          playerCoordinates: {
            origin: 'topLeft',
            dimensions: {
              x: e.clientX,
              y: e.clientY,
            },
            display: {
              x: e.screenX,
              y: e.screenY,
            },
          },
          position: {
            x: 0,
            y: 0,
          },
        };
        send(generators.mouse.down(peerId, detail));
        break;
      }

      case 'mousemove': {
        const e = event as MouseEvent;
        const currentTime = Date.now();
        const timeDelta = currentTime - lastEventTime;
        lastEventTime = currentTime;

        // Try to get delta from movementX/Y first
        let deltaX = e.movementX;
        let deltaY = e.movementY;

        // If movementX/Y are zero or undefined, try calculating from client coordinates
        if (deltaX === 0 && deltaY === 0) {
          deltaX = e.clientX - previousMousePosition.x;
          deltaY = e.clientY - previousMousePosition.y;
        }

        // Debug logging
        // console.log('Mouse Event Debug:', {
        //   movementX: e.movementX,
        //   movementY: e.movementY,
        //   clientX: e.clientX,
        //   clientY: e.clientY,
        //   screenX: e.screenX,
        //   screenY: e.screenY,
        //   deltaX,
        //   deltaY,
        //   timeDelta,
        //   previousPosition: previousMousePosition,
        // });

        // Calculate absolute value for movementX/Y and alert if its greater than 300
        // if (Math.abs(deltaX) > 300 || Math.abs(deltaY) > 300) {
        //   console.log('Mouse movement is too large:', { deltaX, deltaY });
        //   // drop the event
        //   return;
        // }

        // Update previous position using client coordinates
        previousMousePosition = { x: e.clientX, y: e.clientY };

        // Only send if we have non-zero deltas
        if (deltaX !== 0 || deltaY !== 0) {
          send(
            generators.mouse.move(peerId, {
              delta: {
                x: deltaX,
                y: deltaY,
              },
              playerCoordinates: {
                origin: 'topLeft',
                dimensions: {
                  x: e.clientX,
                  y: e.clientY,
                },
                display: {
                  x: e.screenX,
                  y: e.screenY,
                },
              },
              position: {
                x: 0,
                y: 0,
              },
            }),
          );
        }
        break;
      }

      case 'wheel': {
        const e = event as WheelEvent;
        send(
          generators.mouse.wheel(peerId, {
            playerCoordinates: {
              origin: 'topLeft',
              dimensions: {
                x: e.clientX,
                y: e.clientY,
              },
              display: {
                x: e.screenX,
                y: e.screenY,
              },
            },
            position: {
              x: 0,
              y: 0,
            },
            scroll: {
              x: e.deltaX,
              y: -e.deltaY,
            },
            mode: e.deltaMode,
          }),
        );
        break;
      }
    }
  }
};

export const setGamepadState = (gamepadState: MobileGamepadState) => {
  if (hostConnectionState.currentState.status === 'connected') {
    const peerId = hostConnectionState.currentState.playerPeerId;
    send(generators.gamepad.setState(peerId, mobileGamepadToGamepadStateMessage(gamepadState)));
  }
};
